// src/components/PendingApprovals.jsx
// Component to display and manage pending automation approvals
// Shows when automations need agent approval due to recent client contact

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bell,
  Check,
  X,
  Clock,
  MessageSquare,
  User,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  RefreshCw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '';

// Badge component for showing pending count in sidebar/navbar
export function PendingApprovalsBadge({ className = '' }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    const fetchCount = async () => {
      try {
        const res = await fetch(`${API_URL}/api/automation-approvals/count`, {
          credentials: 'include'
        });
        const data = await res.json();
        if (data.ok) {
          setCount(data.count);
        }
      } catch (err) {
        console.error('[PendingApprovals] Failed to fetch count:', err);
      }
    };

    fetchCount();
    // Poll every 60 seconds
    const interval = setInterval(fetchCount, 60000);
    return () => clearInterval(interval);
  }, []);

  if (count === 0) return null;

  return (
    <span className={`bg-red-500 text-white text-xs font-bold rounded-full px-2 py-0.5 ${className}`}>
      {count > 99 ? '99+' : count}
    </span>
  );
}

// Mini widget for dashboard showing pending approvals summary
export function PendingApprovalsWidget({ onViewAll }) {
  const { t } = useTranslation();
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchApprovals = async () => {
      try {
        const res = await fetch(`${API_URL}/api/automation-approvals?status=pending&limit=3`, {
          credentials: 'include'
        });
        const data = await res.json();
        if (data.ok) {
          setApprovals(data.approvals);
        }
      } catch (err) {
        console.error('[PendingApprovals] Failed to fetch:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchApprovals();
  }, []);

  if (loading) return null;
  if (approvals.length === 0) return null;

  return (
    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
      <div className="flex items-center gap-2 mb-3">
        <Bell className="text-yellow-600" size={20} />
        <h3 className="font-semibold text-yellow-800">
          {approvals.length} Pending Approval{approvals.length !== 1 ? 's' : ''}
        </h3>
      </div>
      <p className="text-sm text-yellow-700 mb-3">
        Some automations need your approval before sending because these clients were recently contacted.
      </p>
      <button
        onClick={onViewAll}
        className="text-sm font-medium text-yellow-700 hover:text-yellow-900 underline"
      >
        Review All
      </button>
    </div>
  );
}

// Full modal for managing pending approvals
export function PendingApprovalsModal({ isOpen, onClose }) {
  const { t } = useTranslation();
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState({});
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [expandedId, setExpandedId] = useState(null);

  const fetchApprovals = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/automation-approvals?status=pending`, {
        credentials: 'include'
      });
      const data = await res.json();
      if (data.ok) {
        setApprovals(data.approvals);
      }
    } catch (err) {
      console.error('[PendingApprovals] Failed to fetch:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchApprovals();
    }
  }, [isOpen, fetchApprovals]);

  const handleApprove = async (id) => {
    setProcessing(prev => ({ ...prev, [id]: 'approving' }));
    try {
      const res = await fetch(`${API_URL}/api/automation-approvals/${id}/approve`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await res.json();
      if (data.ok) {
        setApprovals(prev => prev.filter(a => a.id !== id));
        setSelectedIds(prev => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      } else {
        alert(`Failed to send: ${data.error}`);
      }
    } catch (err) {
      alert('Failed to approve: ' + err.message);
    } finally {
      setProcessing(prev => ({ ...prev, [id]: null }));
    }
  };

  const handleDecline = async (id) => {
    setProcessing(prev => ({ ...prev, [id]: 'declining' }));
    try {
      const res = await fetch(`${API_URL}/api/automation-approvals/${id}/decline`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: 'Declined by agent' })
      });
      const data = await res.json();
      if (data.ok) {
        setApprovals(prev => prev.filter(a => a.id !== id));
        setSelectedIds(prev => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }
    } catch (err) {
      alert('Failed to decline: ' + err.message);
    } finally {
      setProcessing(prev => ({ ...prev, [id]: null }));
    }
  };

  const handleBulkApprove = async () => {
    if (selectedIds.size === 0) return;
    setProcessing(prev => ({ ...prev, bulk: 'approving' }));
    try {
      const res = await fetch(`${API_URL}/api/automation-approvals/bulk-approve`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: Array.from(selectedIds) })
      });
      const data = await res.json();
      if (data.ok) {
        fetchApprovals();
        setSelectedIds(new Set());
      }
    } catch (err) {
      alert('Bulk approve failed: ' + err.message);
    } finally {
      setProcessing(prev => ({ ...prev, bulk: null }));
    }
  };

  const handleBulkDecline = async () => {
    if (selectedIds.size === 0) return;
    setProcessing(prev => ({ ...prev, bulk: 'declining' }));
    try {
      const res = await fetch(`${API_URL}/api/automation-approvals/bulk-decline`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: Array.from(selectedIds), reason: 'Bulk declined' })
      });
      const data = await res.json();
      if (data.ok) {
        fetchApprovals();
        setSelectedIds(new Set());
      }
    } catch (err) {
      alert('Bulk decline failed: ' + err.message);
    } finally {
      setProcessing(prev => ({ ...prev, bulk: null }));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (selectedIds.size === approvals.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(approvals.map(a => a.id)));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-3">
            <Bell className="text-yellow-500" size={24} />
            <h2 className="text-xl font-bold">Pending Automation Approvals</h2>
            {approvals.length > 0 && (
              <span className="bg-yellow-100 text-yellow-800 text-sm font-medium px-2.5 py-0.5 rounded">
                {approvals.length} pending
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={fetchApprovals}
              className="p-2 hover:bg-gray-100 rounded-lg"
              title="Refresh"
            >
              <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Bulk Actions */}
        {approvals.length > 0 && (
          <div className="flex items-center gap-4 px-6 py-3 bg-gray-50 border-b">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={selectedIds.size === approvals.length && approvals.length > 0}
                onChange={selectAll}
                className="w-4 h-4 rounded border-gray-300"
              />
              <span className="text-sm text-gray-600">
                {selectedIds.size > 0 ? `${selectedIds.size} selected` : 'Select all'}
              </span>
            </label>
            {selectedIds.size > 0 && (
              <>
                <button
                  onClick={handleBulkApprove}
                  disabled={processing.bulk}
                  className="flex items-center gap-1 px-3 py-1.5 bg-green-500 text-white text-sm font-medium rounded-lg hover:bg-green-600 disabled:opacity-50"
                >
                  <Check size={16} />
                  Approve Selected
                </button>
                <button
                  onClick={handleBulkDecline}
                  disabled={processing.bulk}
                  className="flex items-center gap-1 px-3 py-1.5 bg-red-500 text-white text-sm font-medium rounded-lg hover:bg-red-600 disabled:opacity-50"
                >
                  <X size={16} />
                  Decline Selected
                </button>
              </>
            )}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="animate-spin text-gray-400" size={32} />
            </div>
          ) : approvals.length === 0 ? (
            <div className="text-center py-12">
              <Check className="mx-auto text-green-500 mb-4" size={48} />
              <h3 className="text-lg font-semibold text-gray-700">All caught up!</h3>
              <p className="text-gray-500">No pending approvals at this time.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {approvals.map((approval) => (
                <ApprovalCard
                  key={approval.id}
                  approval={approval}
                  isExpanded={expandedId === approval.id}
                  onToggleExpand={() => setExpandedId(expandedId === approval.id ? null : approval.id)}
                  isSelected={selectedIds.has(approval.id)}
                  onToggleSelect={() => toggleSelect(approval.id)}
                  onApprove={() => handleApprove(approval.id)}
                  onDecline={() => handleDecline(approval.id)}
                  processing={processing[approval.id]}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Individual approval card
function ApprovalCard({
  approval,
  isExpanded,
  onToggleExpand,
  isSelected,
  onToggleSelect,
  onApprove,
  onDecline,
  processing
}) {
  const timeUntilExpiry = () => {
    const expires = new Date(approval.expiresAt);
    const now = new Date();
    const hours = Math.max(0, Math.floor((expires - now) / (1000 * 60 * 60)));
    if (hours < 1) return 'Expires soon';
    if (hours < 24) return `Expires in ${hours}h`;
    return `Expires in ${Math.floor(hours / 24)}d`;
  };

  return (
    <div className={`border rounded-lg ${isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}>
      {/* Card Header */}
      <div className="flex items-center gap-4 p-4">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={onToggleSelect}
          className="w-4 h-4 rounded border-gray-300"
        />

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <User size={16} className="text-gray-400" />
            <span className="font-medium">
              {approval.client.firstName} {approval.client.lastName}
            </span>
            <span className="text-gray-400">|</span>
            <span className="text-sm text-gray-500">
              {approval.automationName}
            </span>
          </div>

          <div className="flex items-center gap-4 text-sm text-gray-500">
            <span className="flex items-center gap-1">
              <AlertTriangle size={14} className="text-yellow-500" />
              Contacted {approval.lastContact.daysSince} day{approval.lastContact.daysSince !== 1 ? 's' : ''} ago
              {approval.lastContact.source && ` via ${approval.lastContact.source}`}
            </span>
            <span className="flex items-center gap-1">
              <Clock size={14} />
              {timeUntilExpiry()}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={onApprove}
            disabled={processing}
            className="flex items-center gap-1 px-3 py-1.5 bg-green-500 text-white text-sm font-medium rounded-lg hover:bg-green-600 disabled:opacity-50"
          >
            {processing === 'approving' ? (
              <RefreshCw size={16} className="animate-spin" />
            ) : (
              <Check size={16} />
            )}
            Send
          </button>
          <button
            onClick={onDecline}
            disabled={processing}
            className="flex items-center gap-1 px-3 py-1.5 bg-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-300 disabled:opacity-50"
          >
            {processing === 'declining' ? (
              <RefreshCw size={16} className="animate-spin" />
            ) : (
              <X size={16} />
            )}
            Skip
          </button>
          <button
            onClick={onToggleExpand}
            className="p-1.5 hover:bg-gray-100 rounded-lg"
          >
            {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </button>
        </div>
      </div>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="px-4 pb-4 pt-0 border-t bg-gray-50">
          <div className="grid grid-cols-2 gap-4 mt-4">
            {/* Message to send */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                <MessageSquare size={16} />
                Message to Send
              </h4>
              <div className="bg-white p-3 rounded border text-sm whitespace-pre-wrap">
                {approval.messageBody}
              </div>
            </div>

            {/* Last contact info */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                <Clock size={16} />
                Recent Contact ({approval.lastContact.daysSince} days ago)
              </h4>
              <div className="bg-white p-3 rounded border text-sm">
                <p className="text-gray-500 mb-1">
                  {approval.lastContact.direction === 'inbound' ? 'Received' : 'Sent'} via {approval.lastContact.source}
                </p>
                <p className="text-gray-700 italic">
                  "{approval.lastContact.preview}"
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Export default as main modal
export default PendingApprovalsModal;
